import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import "../../../src/hr.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  active,
  AttendanceDetails,
  DeActive,
  DeleteStaff,
  fileName,
  HR,
  ImgError,
  ImgSuccess,
  ImgTitle,
  InvalidImage,
  LeaveBalance,
  LeaveSummary,
  Profile,
  Salary,
  SalaryOverView,
  SalaryStructure,
  StaffImageMessage,
} from "../../Constant/hrLayout";
import {
  checkStatusCodeSuccess,
  getImgType,
  getItem,
  getLocalItem,
  setItem,
  staff,
} from "../../Constant/common";
import { activeInactiveStaffApi, deleteStaffApi, hrStaffImageApi } from "../../Api/HRStaffApi";
import { toast } from "react-toastify";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import BaseModal from "../../BaseComponents/BaseModal";
import { StaffDeleteMessage } from "../../Constant/Center";
import { Delete, Yes } from "../../Constant";
import { BaseStaffURL } from "../../Api/Service";
import { fileDownloadApi } from "../../Api/common";
import {
  User,
  DollarSign,
  Calendar,
  CreditCard,
  ChevronDown,
  Trash2,
  ToggleLeft,
  ToggleRight,
  FileText,
} from "react-feather";

const GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#10b981,#06b6d4)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#6366f1)",
];

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const normalizeStaffData = (raw = {}) => ({
  ...raw,
  isActive: raw.isActive === true || raw.isActive === 1 || raw.isActive === "true",
});

const HrLayout = ({ component }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownTriggerRef = useRef(null);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, right: 0 });
  const { setLoading: setLoader } = usePageLoader();
  const [btnLoader, setBtnLoader] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(true);
  const [staffImage, setStaffImage] = useState(null);
  const [staffData, setStaffData] = useState(() =>
    normalizeStaffData(JSON.parse(getItem("staffData")) || {}),
  );

  const location = useLocation();
  const navigate = useNavigate();
  const staffId = useParams("staffId").staffId;
  const permission = JSON.parse(getLocalItem("permissions"));
  const hasSalary = permission?.some(
    (p) => p.parent_module_name === HR && p.module_name === Salary,
  );
  const userRole = getLocalItem("role");
  const hasLetters =
    userRole === "SuperAdmin" ||
    permission?.some(
      (p) =>
        p.parent_module_name?.trim()?.toLowerCase() === "setting" &&
        p.module_name?.trim()?.toLowerCase() === "letter templates",
    );
  const isProfile = JSON.parse(getItem("isProfile") || false);
  const isAdminProfile = useMemo(() => {
    return isProfile
      ? userRole?.trim()?.toLowerCase() === "admin"
      : staffData?.roleModel?.role_name?.trim()?.toLowerCase() === "admin";
  }, [isProfile, userRole, staffData]);

  const avatarGradient = GRADIENTS[parseInt(staffData?.id || "0", 10) % GRADIENTS.length];

  const fetchStaffImage = useCallback(() => {
    setLoader(true);
    const payload = isProfile ? getLocalItem("id") : staffId;
    hrStaffImageApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp.statusCode)) {
          setStaffImage(resp?.data?.staff_image);
        } else {
          setIsImageLoaded(false);
        }
      })
      .catch(() => setIsImageLoaded(false))
      .finally(() => setLoader(false));
  }, [isProfile, staffId, setLoader]);

  useEffect(() => {
    fetchStaffImage();
  }, [fetchStaffImage]);

  useEffect(() => {
    const handler = () => {
      const raw = JSON.parse(getItem("staffData"));
      if (raw) setStaffData(normalizeStaffData(raw));
    };
    window.addEventListener("staffDataUpdated", handler);
    return () => window.removeEventListener("staffDataUpdated", handler);
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = () => setDropdownOpen(false);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const menuItems = [
    ...(isProfile
      ? [
          {
            id: 1,
            title: Profile,
            link: `/profile`,
            icon: User,
            matchPaths: ["/profile", "/staffProfile"],
          },
          ...(hasSalary && !isAdminProfile
            ? [
                {
                  id: 2,
                  title: SalaryOverView,
                  link: `/salaryOverview`,
                  icon: DollarSign,
                  matchPaths: ["/salaryOverview"],
                },
                {
                  id: 3,
                  title: SalaryStructure,
                  link: `/salaryStructure`,
                  icon: DollarSign,
                  matchPaths: [
                    "/salaryStructure",
                    "/editSalaryStructure",
                    "/reviseSalaryStructure",
                    "/salaryHistory",
                  ],
                },
              ]
            : []),
          ...(!isAdminProfile
            ? [
                {
                  id: 4,
                  title: LeaveSummary,
                  link: `/leaveSummary`,
                  icon: Calendar,
                  matchPaths: ["/leaveSummary"],
                },
                {
                  id: 5,
                  title: LeaveBalance,
                  link: `/leaveBalance`,
                  icon: Calendar,
                  matchPaths: ["/leaveBalance"],
                },
              ]
            : []),
        ]
      : [
          {
            id: 1,
            title: Profile,
            link: `/staffProfile/${staffId}`,
            icon: User,
            matchPaths: ["/profile", "/staffProfile"],
          },
          ...(hasSalary && !isAdminProfile
            ? [
                {
                  id: 2,
                  title: SalaryOverView,
                  link: `/salaryOverview/${staffId}`,
                  icon: DollarSign,
                  matchPaths: ["/salaryOverview"],
                },
                {
                  id: 3,
                  title: SalaryStructure,
                  link: `/salaryStructure/${staffId}`,
                  icon: DollarSign,
                  matchPaths: [
                    "/salaryStructure",
                    "/editSalaryStructure",
                    "/reviseSalaryStructure",
                    "/salaryHistory",
                  ],
                },
              ]
            : []),
          {
            id: 4,
            title: AttendanceDetails,
            link: `/attendanceDetails/${staffId}`,
            icon: Calendar,
            matchPaths: ["/attendanceDetails"],
          },
          ...(!isAdminProfile
            ? [
                {
                  id: 5,
                  title: LeaveSummary,
                  link: `/leaveSummary/${staffId}`,
                  icon: Calendar,
                  matchPaths: ["/leaveSummary"],
                },
                {
                  id: 6,
                  title: LeaveBalance,
                  link: `/leaveBalance/${staffId}`,
                  icon: CreditCard,
                  matchPaths: ["/leaveBalance"],
                },
              ]
            : []),
          ...(hasLetters
            ? [
                {
                  id: 7,
                  title: "Letters",
                  link: `/documentLetters/${staffId}`,
                  icon: FileText,
                  matchPaths: ["/documentLetters"],
                },
              ]
            : []),
        ]),
  ];

  const isActive = (item) => {
    if (location.pathname === item.link || location.pathname.startsWith(item.link)) return true;
    if (item.matchPaths) {
      return item.matchPaths.some((p) => location.pathname.startsWith(p));
    }
    return false;
  };

  const handleSelectAction = (action) => {
    setDropdownOpen(false);
    setBtnLoader(true);
    if (action === active || action === DeActive) {
      const nextActive = action === active;
      activeInactiveStaffApi(staffId, { isActive: nextActive })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            const newData = { ...staffData, isActive: nextActive };
            setStaffData(newData);
            setItem("staffData", JSON.stringify(newData));
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => setBtnLoader(false));
    } else if (action === DeleteStaff) {
      deleteStaffApi(staffId, { is_deleted: true })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            navigate("/staff");
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => setBtnLoader(false));
    }
  };

  const downloadImage = async (imgFile, imgName) => {
    const contentType = getImgType(imgFile);
    setBtnLoader(true);
    try {
      const response = await fileDownloadApi({ fileName: `${staff}/${imgFile}` });
      if (!response) {
        toast.error(InvalidImage);
        return;
      }
      const file = new Blob([response], { type: contentType });
      const url = window.URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = imgName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(ImgSuccess);
      setModalOpen(false);
    } catch {
      toast.error(ImgError);
    } finally {
      setBtnLoader(false);
    }
  };

  const statusColor = staffData?.isActive ? "#10b981" : "#f43f5e";
  const statusBg = staffData?.isActive ? "rgba(16,185,129,0.12)" : "rgba(244,63,94,0.12)";

  return (
    <>
      {/* Delete confirm modal */}
      <BaseModal
        isOpen={modalDelete}
        title="Delete Staff"
        toggler={() => setModalDelete(false)}
        submit={() => handleSelectAction(DeleteStaff)}
        submitText={Delete}
        disabled={btnLoader}
        loader={btnLoader}
      >
        {StaffDeleteMessage}
      </BaseModal>

      {/* Image preview modal */}
      <BaseModal
        isOpen={modalOpen}
        title={ImgTitle}
        toggler={() => setModalOpen(false)}
        submit={() => {
          if (isImageLoaded) downloadImage(staffImage, `${staffData.id}${fileName}`);
          else toast.error(ImgError);
        }}
        submitText={Yes}
        disabled={btnLoader}
        loader={btnLoader}
      >
        {StaffImageMessage}
      </BaseModal>

      {/* ── Profile hero banner ── */}
      <div
        className="animate-slide-down"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 20,
          marginBottom: 20,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Gradient top bar */}
        <div
          style={{
            height: 4,
            background: "linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)",
          }}
        />

        {/* Decorative orb */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            right: -60,
            top: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(99,102,241,0.06)",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          {/* Left — avatar + info */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {/* Avatar */}
            <div
              onClick={() => isImageLoaded && setModalOpen(true)}
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                flexShrink: 0,
                background: staffImage ? "transparent" : avatarGradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(99,102,241,0.3)",
                border: "2px solid rgba(99,102,241,0.25)",
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              {staffImage ? (
                <img
                  src={`${BaseStaffURL}${staffImage}?t=${Date.now()}`}
                  alt={Profile}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    setIsImageLoaded(false);
                  }}
                />
              ) : (
                <span style={{ fontSize: "1.25rem", fontWeight: 800, color: "#fff" }}>
                  {getInitials(staffData?.name)}
                </span>
              )}
            </div>

            {/* Name + ID + status */}
            <div>
              <h2
                style={{
                  fontSize: "1.125rem",
                  fontWeight: 800,
                  margin: 0,
                  marginBottom: 4,
                  color: "var(--text-primary)",
                }}
              >
                {staffData?.name || "—"}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {staffData?.id && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      fontFamily: "monospace",
                      color: "var(--text-muted)",
                      background: "var(--icon-btn-bg)",
                      padding: "2px 8px",
                      borderRadius: 6,
                      border: "1px solid var(--glass-border)",
                    }}
                  >
                    {staffData.id}
                  </span>
                )}
                <span
                  style={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    padding: "2px 10px",
                    borderRadius: 999,
                    background: statusBg,
                    color: statusColor,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {staffData?.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          {/* Right — actions (only for non-profile view) */}
          {!isProfile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Status dropdown */}
              <div style={{ position: "relative" }}>
                <button
                  ref={dropdownTriggerRef}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    if (!dropdownOpen && dropdownTriggerRef.current) {
                      const rect = dropdownTriggerRef.current.getBoundingClientRect();
                      setDropdownCoords({
                        top: rect.bottom + window.scrollY + 6,
                        right: window.innerWidth - rect.right,
                      });
                    }
                    setDropdownOpen((o) => !o);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 14px",
                    background: statusBg,
                    border: `1px solid ${statusColor}40`,
                    borderRadius: 10,
                    cursor: "pointer",
                    color: statusColor,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    transition: "all 0.15s",
                  }}
                >
                  {staffData?.isActive ? (
                    <ToggleRight size={15} strokeWidth={2} />
                  ) : (
                    <ToggleLeft size={15} strokeWidth={2} />
                  )}
                  {staffData?.isActive ? active : DeActive}
                  <ChevronDown
                    size={13}
                    strokeWidth={2}
                    style={{
                      transform: dropdownOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </button>

                {dropdownOpen &&
                  ReactDOM.createPortal(
                    <div
                      className="animate-scale-in"
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: dropdownCoords.top,
                        right: dropdownCoords.right,
                        background: "var(--dropdown-bg)",
                        border: "1px solid var(--dropdown-border)",
                        borderRadius: 12,
                        boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
                        backdropFilter: "blur(20px)",
                        minWidth: 160,
                        padding: 6,
                        zIndex: 9999,
                      }}
                    >
                      {[active, DeActive].map((a) => {
                        const isSelected = staffData?.isActive === (a === active);
                        return (
                          <button
                            key={a}
                            onClick={() => handleSelectAction(a)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              width: "100%",
                              padding: "8px 12px",
                              borderRadius: 8,
                              background: isSelected ? "rgba(99,102,241,0.1)" : "transparent",
                              border: "none",
                              cursor: "pointer",
                              color: a === active ? "#10b981" : "#f43f5e",
                              fontSize: "0.8125rem",
                              fontWeight: isSelected ? 700 : 500,
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "var(--nav-hover)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = isSelected
                                ? "rgba(99,102,241,0.1)"
                                : "transparent";
                            }}
                          >
                            {a === active ? (
                              <ToggleRight size={14} strokeWidth={2} />
                            ) : (
                              <ToggleLeft size={14} strokeWidth={2} />
                            )}
                            <span style={{ flex: 1, textAlign: "left" }}>{a}</span>
                            {isSelected && (
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color: "var(--brand-indigo)",
                                  fontWeight: 700,
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })}
                      <div
                        style={{ height: 1, background: "var(--glass-border)", margin: "4px 0" }}
                      />
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          setModalDelete(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--danger)",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(244,63,94,0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <Trash2 size={14} strokeWidth={2} /> {DeleteStaff}
                      </button>
                    </div>,
                    document.body,
                  )}
              </div>
            </div>
          )}
        </div>

        {/* ── Tab navigation ── */}
        <div
          style={{
            display: "flex",
            gap: 2,
            padding: "0 16px",
            borderTop: "1px solid var(--glass-border)",
            overflowX: "auto",
          }}
        >
          {menuItems.map((item) => {
            const ItemIcon = item.icon;
            const active_ = isActive(item);
            return (
              <Link
                key={item.id}
                to={item.link}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "12px 16px",
                  color: active_ ? "var(--brand-indigo)" : "var(--text-muted)",
                  fontWeight: active_ ? 600 : 500,
                  fontSize: "0.8125rem",
                  textDecoration: "none",
                  borderBottom: active_ ? "2px solid var(--brand-indigo)" : "2px solid transparent",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!active_) e.currentTarget.style.color = "var(--brand-indigo)";
                }}
                onMouseLeave={(e) => {
                  if (!active_) e.currentTarget.style.color = "var(--text-muted)";
                }}
              >
                <ItemIcon size={14} strokeWidth={2} />
                {item.title}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Page content ── */}
      <div className="animate-fade-in">{component}</div>
    </>
  );
};

export default HrLayout;
